import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import type { SessionContentFields } from "@/types/sessionContentFields"

import { Depot } from "../types/depot"

export interface Session {
  id: string
  created_at: string
  title: string
  date: string
  start_time: string
  end_time: string
  depot_id: string
  capacity: number
  status: "Open" | "Closed" | "Deleted"
  jobs: string[]
  depot?: Depot // Add depot relationship
  type?: "orientation" | "vehicle_collection" // Session type
  content_fields?: SessionContentFields | null
  registered?: number
}

// The columns a session write may set. `depot` and `registered` come from the
// join and the booking count, so they can never be written back.
export type SessionWrite = Omit<Session, "id" | "created_at" | "depot" | "registered">

export interface SessionFilters {
  depot_id?: string
  status?: "Open" | "Closed" | "Deleted"
  date_from?: string
  date_to?: string
  search?: string
  type?: "orientation" | "vehicle_collection"
}

interface UseSessionsOptions {
  filters?: SessionFilters
}

export const useSessions = ({ filters = {} }: UseSessionsOptions = {}) => {
  const queryClient = useQueryClient()

  // Define the page result type
  interface SessionsPage {
    sessions: Session[]
  }

  // Function to fetch sessions with pagination and filters
  const fetchSessions = async (): Promise<SessionsPage> => {
    let query = supabase
      .from("sessions")
      .select("*, depot:depots(*), bookings:session_bookings(*)")
      .order("date", { ascending: true })
      .order("start_time", { ascending: true })
    // Apply filters
    if (filters.depot_id) {
      query = query.eq("depot_id", filters.depot_id)
    }

    if (filters.type) {
      query = query.eq("type", filters.type)
    }
    if (filters.date_from || filters.date_to) {
      if (filters.date_from) {
        query = query.gte("date", filters.date_from)
      }

      if (filters.date_to) {
        query = query.lte("date", filters.date_to)
      }
    } else {
      query = query.gte("date", new Date().toISOString())
    }

    const { data, error } = await query

    if (error) {
      throw error
    }

    return {
      sessions: data.map((session) => ({
        ...session,
        registered:
          session.bookings?.filter(
            (booking: { cancelled_at?: string | null }) => !booking.cancelled_at,
          ).length || 0,
      })) as Session[],
    }
  }

  // Query for fetching sessions
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, error } = useInfiniteQuery<
    SessionsPage,
    Error,
    InfiniteData<SessionsPage>,
    [string, SessionFilters],
    number
  >({
    queryKey: ["sessions", filters],
    queryFn: fetchSessions,
    initialPageParam: 0,
    getNextPageParam: () => undefined,
  })

  // Flatten the pages of sessions
  const sessions = data?.pages.flatMap((page) => page.sessions) || []

  // Mutation for creating a new session
  const createSession = useMutation<
    Session, // Return type
    Error, // Error type
    SessionWrite // Variables type
  >({
    mutationFn: async (newSession) => {
      const { data, error } = await supabase.from("sessions").insert(newSession).select().single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      // Invalidate the sessions query to refetch
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })

  // Mutation for updating a session
  const updateSession = useMutation<
    Session, // Return type
    Error, // Error type
    Partial<SessionWrite> & { id: string } // Variables type
  >({
    mutationFn: async (updatedSession) => {
      const { id, ...sessionData } = updatedSession
      const { data, error } = await supabase
        .from("sessions")
        .update(sessionData)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })

  // Mutation for deleting a session
  const deleteSession = useMutation<
    Session, // Return type
    Error, // Error type
    string // Variables type (session id)
  >({
    mutationFn: async (id) => {
      // Soft delete by updating status to 'Deleted'
      const { data, error } = await supabase
        .from("sessions")
        .update({ status: "Deleted" })
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })

  return {
    sessions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    createSession,
    updateSession,
    deleteSession,
  }
}

// Hook for creating sessions (can be used independently)
export const useCreateSession = () => {
  const queryClient = useQueryClient()

  return useMutation<
    Session, // Return type
    Error, // Error type
    SessionWrite // Variables type
  >({
    mutationFn: async (newSession) => {
      const { data, error } = await supabase.from("sessions").insert(newSession).select().single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })
}

// Hook for updating sessions
export const useUpdateSession = () => {
  const queryClient = useQueryClient()

  return useMutation<
    Session, // Return type
    Error, // Error type
    Partial<SessionWrite> & { id: string } // Variables type
  >({
    mutationFn: async (updatedSession) => {
      const { id, ...sessionData } = updatedSession
      const { data, error } = await supabase
        .from("sessions")
        .update(sessionData)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })
}

// Hook for deleting sessions
export const useDeleteSession = () => {
  const queryClient = useQueryClient()

  return useMutation<
    Session, // Return type
    Error, // Error type
    string // Variables type (session id)
  >({
    mutationFn: async (id) => {
      // Soft delete by updating status to 'Deleted'
      const { data, error } = await supabase
        .from("sessions")
        .update({ status: "Deleted" })
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data as Session
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] })
    },
  })
}
